import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import { Loader } from '~/client/src/shared/components/Loader'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import DeliveryUnitsStore from '~/client/src/shared/stores/domain/DeliveryUnits.store'
import MaterialCategoryStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import SharedDeliveriesStore from '~/client/src/shared/stores/domain/SharedDeliveries.store'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import DeliveryMaterialsCellModal from './components/DeliveryMaterialsCellModal'
import DeliveryMaterialsCellStore, {
  ADDITIONAL_TAGS_WIDTH,
} from './DeliveryMaterialsCell.store'

import './DeliveryMaterialsCell.scss'

// localization: no display text to translate

interface IProps {
  deliveryId: string
  onAddMaterialClick: (deliveryId: string, shouldAddMaterial?: boolean) => void

  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoryStore
  eventsStore?: DesktopEventStore
  deliveriesStore?: DeliveriesStore
  deliveryUnitsStore?: DeliveryUnitsStore
  sharedDeliveriesStore?: SharedDeliveriesStore
}

@inject(
  'materialsStore',
  'materialCategoryStore',
  'eventsStore',
  'deliveriesStore',
  'deliveryUnitsStore',
  'sharedDeliveriesStore',
)
@observer
export default class DeliveryMaterialsCell extends React.Component<IProps> {
  private readonly store: DeliveryMaterialsCellStore = null
  @observable private isPopoverOpen: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)

    this.store = new DeliveryMaterialsCellStore(
      props.deliveryId,
      props.materialsStore,
      props.materialCategoryStore,
      props.eventsStore,
      props.deliveriesStore,
      props.deliveryUnitsStore,
      props.sharedDeliveriesStore,
    )
  }

  public componentDidUpdate(prevProps: IProps) {
    const { deliveryId } = this.props
    if (deliveryId !== prevProps.deliveryId) {
      this.store.init(deliveryId)
    }
  }

  public render() {
    const { isLoading, deliveryMaterials } = this.store

    if (isLoading) {
      return <Loader />
    }

    if (!deliveryMaterials?.length) {
      return NO_VALUE
    }

    const { deliveryId } = this.props

    return (
      <Popover open={this.isPopoverOpen} onOpenChange={this.setPopoverOpen}>
        <PopoverTrigger
          nativeButton={false}
          render={<div className="full-height full-width pointer" />}
        >
          <DynamicOverflowList
            items={this.deliveryMaterialTags}
            additionalWidth={ADDITIONAL_TAGS_WIDTH}
            allowRecalculationForItems={true}
          >
            {({ visibleElements, overflowElements, containerRefSetter }) => {
              return (
                <div
                  className="full-height full-width pointer"
                  ref={containerRefSetter}
                >
                  {visibleElements?.length ? visibleElements : NO_VALUE}
                  {!!overflowElements?.length && (
                    <div className="inline-block">...</div>
                  )}
                </div>
              )
            }}
          </DynamicOverflowList>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="delivery-materials-cell-modal tw:w-auto"
        >
          <DeliveryMaterialsCellModal
            deliveryId={deliveryId}
            materials={deliveryMaterials}
            onAddMaterialClick={this.onAddMaterialClick}
            store={this.store}
          />
        </PopoverContent>
      </Popover>
    )
  }

  @action.bound
  private setPopoverOpen(isOpen: boolean) {
    this.isPopoverOpen = isOpen
  }

  private onAddMaterialClick = (
    deliveryId: string,
    shouldAddMaterial?: boolean,
  ) => {
    this.setPopoverOpen(false)
    this.props.onAddMaterialClick(deliveryId, shouldAddMaterial)
  }

  private get deliveryMaterialTags(): JSX.Element[] {
    return this.store.deliveryMaterialsLabels.map((mat, idx) => (
      <SitemapAttributeTag
        key={idx}
        shouldShowAsTag={true}
        className="mx2 material-tag"
        contentContainerClassName="text-ellipsis py2"
      >
        <span title={mat} className="text large">
          {mat}
        </span>
      </SitemapAttributeTag>
    ))
  }
}
